import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ImageUploader } from "@/components/v2/ImageUploader";
import { useToast } from "@/components/v2/Toast";
import {
  useUpdateUserMe,
  useUploadUserAvatar,
  useUserMe,
} from "@/lib/user-me";
import { useRegisterStep } from "./OnboardingShell";
import { StepShell, StepBody } from "./_StepShell";
import { StepHero } from "./_StepHero";
import { StepFooter } from "./_StepFooter";

/**
 * Step 1 — Personal account. The OWNER user's name + (optional) avatar.
 * Required because the rest of the app addresses operators by first name.
 */
export function PersonalStep() {
  const me = useUserMe();
  const update = useUpdateUserMe();
  const uploadAvatar = useUploadUserAvatar();
  const toast = useToast();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  // Hydrate once the user record arrives (e.g. after refresh mid-wizard).
  useEffect(() => {
    if (me.data) {
      setFirstName(me.data.firstName ?? "");
      setLastName(me.data.lastName ?? "");
    }
  }, [me.data]);

  useRegisterStep({
    onContinue: async () => {
      try {
        await update.mutateAsync({
          firstName: firstName.trim(),
          lastName: lastName.trim() || undefined,
        });
      } catch (e) {
        toast.error("Couldn't save", (e as Error).message);
        throw e;
      }
    },
    disabled: firstName.trim().length === 0,
  });

  return (
    <StepShell variant="form">
      <StepHero
        eyebrow="Step 1 of 5"
        title="Tell us about yourself"
        subtitle="We'll show your name on bookings and team activity."
      />

      <StepBody>
        <div className="space-y-2">
          <Label className="text-[13px] font-medium">Profile photo</Label>
          <ImageUploader
            shape="circle"
            size={80}
            label="Add a photo"
            value={me.data?.avatarUrl}
            onUpload={(file) => uploadAvatar.mutateAsync(file)}
            helpText="Optional. PNG or JPG, up to 5 MB."
          />
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="firstName" className="text-[13px] font-medium">
              First name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="firstName"
              placeholder="Jane"
              autoFocus
              autoComplete="given-name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="h-11"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="lastName" className="text-[13px] font-medium">
              Last name
            </Label>
            <Input
              id="lastName"
              placeholder="Doe"
              autoComplete="family-name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="h-11"
            />
          </div>
        </div>
      </StepBody>

      <StepFooter />
    </StepShell>
  );
}
